import { useTranslation } from "@sagot/i18n";
import { Button, cn, Icon, Kbd, Tabs, TabsContent, TabsList, TabsTrigger, toast } from "@sagot/ui";
import { z } from "zod";
import { useMembers } from "../../../roles/use-roles";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useAgentName } from "../shared/use-engage-data";
import type { useConversationActions } from "./inbox-api";
import type { ConversationItem } from "./inbox-model";
import { useInboxStore, type ComposerMode } from "./inbox-store";
import { SavedRepliesMenu } from "./saved-replies-menu";
import type { InboxPermissions } from "./use-inbox-permissions";

export const COMPOSER_ID = "inbox-composer";
const MESSAGE_MAX = 4000;
const messageSchema = z.string().trim().min(1).max(MESSAGE_MAX);
const toMode = (value: string): ComposerMode => (value === "note" ? "note" : "reply");

interface ComposerProps {
  conversation: ConversationItem;
  actions: ReturnType<typeof useConversationActions>;
  permissions: InboxPermissions;
  /** Takes over from the AI (no-op when already paused); resolves once paused. */
  ensureTakeover: () => Promise<void>;
}

function useMentions() {
  const members = useMembers();
  return (body: string) =>
    (members.data ?? []).filter((m) => m.status === "active" && body.includes(`@${m.name.split(/\s+/)[0] ?? m.name}`)).map((m) => m.user_id);
}

/** Reply / internal-note composer with the AI lock, saved replies and "Draft with AI". */
export function Composer({ conversation, actions, permissions, ensureTakeover }: ComposerProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agentName = useAgentName();
  const errorMessage = useErrorMessage();
  const mentionsOf = useMentions();
  const mode = useInboxStore((s) => s.composerMode);
  const setMode = useInboxStore((s) => s.setComposerMode);
  const text = useInboxStore((s) => s.drafts[conversation.id] ?? "");
  const setDraft = useInboxStore((s) => s.setDraft);
  const note = mode === "note";
  const locked = !note && !conversation.ai_paused && !conversation.needs_staff;
  const enabled = permissions.reply && !locked;
  const sending = actions.reply.isPending || actions.note.isPending;
  const setText = (value: string) => { setDraft(conversation.id, value); };

  const send = () => {
    const parsed = messageSchema.safeParse(text);
    if (!parsed.success) {
      toast.warning(t("engage.inbox.composer.empty"));
      return;
    }
    const pending = note ? actions.note.mutateAsync({ body: parsed.data, mentions: mentionsOf(parsed.data) }) : actions.reply.mutateAsync(parsed.data);
    pending.then(
      () => {
        setText("");
        toast.success(note ? t("engage.inbox.composer.noteAdded") : t("engage.inbox.composer.sent"));
      },
      (error: unknown) => toast.error(errorMessage(error) || t("engage.inbox.composer.sendFailed")),
    );
  };

  const insert = async (value: string) => {
    if (note) setMode("reply");
    await ensureTakeover();
    setText(text ? `${text}\n${value}` : value);
    document.getElementById(COMPOSER_ID)?.focus();
  };

  const draft = async () => {
    try {
      if (note) setMode("reply");
      await ensureTakeover();
      const result = await actions.draft.mutateAsync();
      setText(result.draft);
      toast.info(t("engage.inbox.composer.draftReady"));
      document.getElementById(COMPOSER_ID)?.focus();
    } catch (error) {
      toast.error(errorMessage(error) || t("engage.inbox.composer.draftFailed"));
    }
  };

  const channel = t(`engage.channels.${conversation.channel}`, { defaultValue: conversation.channel });
  const box = (
    <div className={cn("mt-1 rounded-lg border focus-within:border-primary", note ? "border-accent bg-accent-soft" : "border-line-strong bg-surface")}>
      {locked && permissions.reply ? (
        <div className="flex flex-wrap items-center gap-2 rounded-t-lg border-b border-line bg-sunken px-3 py-2 text-xs text-fg-muted" data-testid="composer-lock">
          <Icon name="bot" className="size-4 text-primary-ink" />
          <span className="hidden min-w-0 flex-1 sm:inline">{t("engage.inbox.composer.locked")}</span>
          {permissions.takeover ? (
            <Button size="sm" className="shrink-0 max-sm:w-full" data-shortcut-target="reply" onClick={() => { void ensureTakeover(); }}>
              <Icon name="headset" />
              {t("engage.inbox.thread.takeOver")}
            </Button>
          ) : null}
        </div>
      ) : null}
      {!permissions.reply ? <div className="border-b border-line px-3 py-2 text-xs text-fg-muted">{t("engage.inbox.composer.readOnly")}</div> : null}
      <textarea
        id={COMPOSER_ID}
        data-shortcut-target="reply"
        rows={2}
        aria-label={t("engage.inbox.composer.label")}
        className="w-full resize-none bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-fg-subtle disabled:cursor-not-allowed"
        placeholder={note ? t("engage.inbox.composer.notePlaceholder") : t("engage.inbox.composer.replyPlaceholder")}
        maxLength={MESSAGE_MAX}
        disabled={!enabled}
        value={text}
        onChange={(e) => { setText(e.target.value); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
      />
      <div className="flex flex-wrap items-center gap-1 border-t border-line px-2 py-1.5">
        <SavedRepliesMenu disabled={!permissions.reply || (locked && !permissions.takeover)} values={{ agent_name: agentName(conversation.agent_id), workspace: summary.name }} onInsert={(v) => { void insert(v); }} />
        <Button variant="ghost" size="sm" loading={actions.draft.isPending} disabled={!permissions.reply || (locked && !permissions.takeover)} onClick={() => { void draft(); }}>
          <Icon name="sparkles" />
          {actions.draft.isPending ? t("engage.inbox.composer.drafting") : t("engage.inbox.composer.draftWithAi")}
        </Button>
        <span className="ml-auto hidden text-[11px] text-fg-subtle 2xl:inline">
          <Kbd>Enter</Kbd> {t("engage.inbox.composer.enterToSend")}
        </span>
        <Button size="sm" className="ml-auto 2xl:ml-0" disabled={!enabled} loading={sending} onClick={send}>
          <Icon name="send" />
          {note ? t("engage.inbox.composer.addNote") : t("engage.inbox.composer.send")}
        </Button>
      </div>
    </div>
  );
  return (
    <div className="shrink-0 border-t border-line bg-surface px-4 pb-4 pt-2 lg:px-6">
      <Tabs value={mode} onValueChange={(v) => { setMode(toMode(v)); }}>
        <div className="flex items-center justify-between gap-2">
          <TabsList aria-label={t("engage.inbox.composer.tabs")} className="border-b-0">
            <TabsTrigger value="reply">{t("engage.inbox.composer.reply")}</TabsTrigger>
            <TabsTrigger value="note">
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <Icon name="lock" className="size-3.5" />
                {t("engage.inbox.composer.note")}
              </span>
            </TabsTrigger>
          </TabsList>
          <span className="hidden text-[11px] text-fg-subtle sm:inline">
            {note ? (
              <span className="font-semibold text-accent-ink">{t("engage.inbox.composer.staffOnly")}</span>
            ) : (
              <>
                {t("engage.inbox.composer.replyingOn", { channel })} <strong className="text-fg-muted">{summary.name}</strong>
              </>
            )}
          </span>
        </div>
        <TabsContent value="reply" className="outline-none">
          {note ? null : box}
        </TabsContent>
        <TabsContent value="note" className="outline-none">
          {note ? box : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
